'use client';

/**
 * ChangeStrapButton
 *
 * Button with circular strap preview that opens the change strap modal.
 * Shows a cropped portion of the watch image focused on the strap area.
 *
 * Ported from breitling-storefront-next/src/views/WatchView/components/ChangeStrapButton/
 *
 * @example
 * ```tsx
 * <ChangeStrapButton
 *   strapImage="/path/to/watch.jpg"
 *   strapName="Black Alligator Leather"
 *   strapPreviewCoords={{ x1: 415, x2: 800, y1: 840, y2: 900 }}
 *   onClick={() => modalActions.open('change-strap', { bundleVariations: [...] })}
 * />
 * ```
 */

import { CropImage } from '@/components/ui/CropImage';
import type { StrapPreviewCoords } from '@/lib/product/types';
import { useTranslations } from 'next-intl';
import styles from './ChangeStrapButton.module.scss';

interface ChangeStrapButtonProps {
  /** URL of the full watch image */
  strapImage: string;
  /** Alt text for the strap image */
  strapName: string;
  /** Coordinates for cropping to the strap area */
  strapPreviewCoords?: StrapPreviewCoords;
  /** Click handler - typically opens the change strap modal */
  onClick: () => void;
}

export function ChangeStrapButton({
  strapImage,
  strapName,
  strapPreviewCoords,
  onClick,
}: ChangeStrapButtonProps) {
  const t = useTranslations('common');

  return (
    <button
      type="button"
      className={styles.changeStrapButton}
      onClick={onClick}
      aria-label={t('changeStrap.button', { defaultValue: 'Change strap' })}
    >
      <div className={styles.imageContainer}>
        <CropImage
          src={strapImage}
          alt={strapName}
          coords={strapPreviewCoords}
          width={36}
          height={36}
        />
      </div>
      <span className={styles.label}>{t('changeStrap.change', { defaultValue: 'Change' })}</span>
      <svg
        className={styles.chevron}
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 1L6.5 6L1.5 11"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
